import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import Input from "../../../components/ui/Input";
import Select from "../../../components/ui/Select";
import { Link } from "react-router-dom";

const DemoBookingSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  console.log("API URL", import.meta.env.VITE_BACKEND_URL);
  const [formData, setFormData] = useState({
    institutionName: "",
    contactName: "",
    email: "",
    phone: "",
    institutionType: "",
    institutionTypeOther: "",
    studentCount: "",
    currentSystem: "",
    currentSystemOther: "",
    primaryChallenge: "",
    primaryChallengeOther: "",
    preferredDate: "",
    preferredTime: "",
    additionalNotes: "",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess] = useState(false);
  const [toast, setToast] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);
  const sectionRef = useRef(null);

  const institutionTypes = [
    { value: "k12-school", label: "K-12 School" },
    { value: "college", label: "College/University" },
    { value: "vocational", label: "Vocational Institute" },
    { value: "international", label: "International School" },
    { value: "boarding", label: "Boarding School" },
    { value: "other", label: "Other" },
  ];

  const studentCountRanges = [
    { value: "0-500", label: "< 500 students" },
    { value: "500-1000", label: "500 - 1,000 students" },
    { value: "1000-2500", label: "1,000 - 2,500 students" },
    { value: "2500-5000", label: "2,500 - 5,000 students" },
    { value: "5000-10000", label: "5,000 - 10,000 students" },
    { value: "10000+", label: "10,000+ students" },
  ];

  const currentSystems = [
    { value: "spreadsheets", label: "Excel/Google Sheets" },
    { value: "legacy-system", label: "Legacy Management System" },
    { value: "multiple-tools", label: "Multiple Disconnected Tools" },
    { value: "manual-processes", label: "Mostly Manual Processes" },
    { value: "other-ems", label: "Other Education Management System" },
    { value: "none", label: "No Current System" },
    { value: "other", label: "Other" },
  ];

  const primaryChallenges = [
    { value: "fee-reconciliation", label: "Fee Collection & Reconciliation" },
    { value: "attendance-tracking", label: "Attendance Management" },
    { value: "data-scattered", label: "Scattered Student Data" },
    { value: "manual-processes", label: "Too Many Manual Processes" },
    { value: "reporting-delays", label: "Delayed Reporting & Analytics" },
    { value: "parent-communication", label: "Parent Communication" },
    { value: "staff-efficiency", label: "Staff Productivity Issues" },
    { value: "compliance", label: "Compliance & Security Concerns" },
    { value: "other", label: "Other" },
  ];

  const timeSlots = [
    { value: "09:00", label: "9:00 AM" },
    { value: "09:30", label: "9:30 AM" },
    { value: "10:00", label: "10:00 AM" },
    { value: "10:30", label: "10:30 AM" },
    { value: "11:00", label: "11:00 AM" },
    { value: "11:30", label: "11:30 AM" },
    { value: "12:00", label: "12:00 PM" },
    { value: "12:30", label: "12:30 PM" },
    { value: "13:00", label: "1:00 PM" },
    { value: "13:30", label: "1:30 PM" },
    { value: "14:00", label: "2:00 PM" },
    { value: "14:30", label: "2:30 PM" },
    { value: "15:00", label: "3:00 PM" },
    { value: "15:30", label: "3:30 PM" },
    { value: "16:00", label: "4:00 PM" },
    { value: "16:30", label: "4:30 PM" },
    { value: "17:00", label: "5:00 PM" },
    { value: "17:30", label: "5:30 PM" },
  ];

  useEffect(() => {
    if (typeof window !== "undefined" && window.innerWidth < 640) {
      setIsVisible(true);
    }
  }, []);

  useEffect(() => {
    if (!("IntersectionObserver" in window)) {
      setIsVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1, rootMargin: "0px 0px -10% 0px" }
    );

    if (sectionRef?.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }

    // Auto-detect institution size and pre-fill student count
    if (field === "institutionName" && value?.length > 3) {
      // Mock auto-detection logic
      const institutionKeywords = {
        university: "5000-10000",
        college: "2500-5000",
        school: "1000-2500",
        academy: "500-1000",
      };

      const detectedSize = Object.entries(institutionKeywords)?.find(
        ([keyword]) => value?.toLowerCase()?.includes(keyword)
      );

      if (detectedSize && !formData?.studentCount) {
        setFormData((prev) => ({ ...prev, studentCount: detectedSize?.[1] }));
      }
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData?.institutionName?.trim()) {
      newErrors["institutionName"] = "Institution name is required";
    }

    if (!formData?.contactName?.trim()) {
      newErrors["contactName"] = "Contact name is required";
    }

    if (!formData?.email?.trim()) {
      newErrors["email"] = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
      newErrors["email"] = "Please enter a valid email address";
    }

    if (!formData?.phone?.trim()) {
      newErrors["phone"] = "Phone number is required";
    }

    if (!formData?.institutionType) {
      newErrors["institutionType"] = "Please select institution type";
    }
    if (
      formData?.institutionType === "other" &&
      !formData?.institutionTypeOther?.trim()
    ) {
      newErrors["institutionTypeOther"] = "Please specify the institution type";
    }

    if (!formData?.studentCount) {
      newErrors["studentCount"] = "Please select student count range";
    }

    if (
      formData?.currentSystem === "other" &&
      !formData?.currentSystemOther?.trim()
    ) {
      newErrors["currentSystemOther"] = "Please specify the current system";
    }

    if (
      formData?.primaryChallenge === "other" &&
      !formData?.primaryChallengeOther?.trim()
    ) {
      newErrors["primaryChallengeOther"] =
        "Please specify the primary challenge";
    }

    if (!formData?.preferredDate) {
      newErrors["preferredDate"] = "Please select preferred date";
    }

    if (!formData?.preferredTime) {
      newErrors["preferredTime"] = "Please select preferred time";
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e?.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = { ...formData };
      await axios.post(`${import.meta.env.VITE_API_URL}/demoRequests`, payload);

      setToast({
        type: "success",
        message: "Demo request submitted successfully!",
      });

      // Reset form after success
      setFormData({
        institutionName: "",
        contactName: "",
        email: "",
        phone: "",
        institutionType: "",
        institutionTypeOther: "",
        studentCount: "",
        currentSystem: "",
        currentSystemOther: "",
        primaryChallenge: "",
        primaryChallengeOther: "",
        preferredDate: "",
        preferredTime: "",
        additionalNotes: "",
      });

      setTimeout(() => setToast(null), 3000);
      // If you prefer the full success page instead of a toast:
      // setShowSuccess(true);
    } catch (error) {
      setToast({
        type: "error",
        message: "Submission failed. Please try again.",
      });
      setTimeout(() => setToast(null), 3500);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getTomorrowDate = () => {
    const tomorrow = new Date();
    tomorrow?.setDate(tomorrow?.getDate() + 1);
    return tomorrow?.toISOString()?.split("T")?.[0];
  };

  if (showSuccess) {
    return (
      <section
        ref={sectionRef}
        id="demo-booking"
        className="py-10 sm:py-16 lg:py-20 bg-gradient-to-b from-surface to-background"
      >
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="text-center">
            <div className="w-24 h-24 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-8">
              <Icon
                name="CheckCircle"
                size={48}
                color="var(--color-success)"
                className="w-full h-full"
                strokeWidth={2}
              />
            </div>

            <h2 className="font-jakarta font-800 text-4xl text-foreground mb-6">
              Demo Booked Successfully!
            </h2>

            <p className="font-inter font-500 text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
              Thank you for your interest in KnowTran. We've received your demo
              request and will send you a calendar invitation shortly.
            </p>

            <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 border border-white/20 aurora-glass mb-8">
              <h3 className="font-jakarta font-700 text-xl text-foreground mb-6">
                What Happens Next?
              </h3>

              <div className="grid md:grid-cols-3 gap-6 text-left">
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="font-jakarta font-700 text-sm text-primary">
                      1
                    </span>
                  </div>
                  <div>
                    <h4 className="font-jakarta font-600 text-foreground mb-2">
                      Calendar Invitation
                    </h4>
                    <p className="font-inter font-500 text-sm text-muted-foreground">
                      You'll receive a calendar invitation within 15 minutes
                      with meeting details
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-success/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="font-jakarta font-700 text-sm text-success">
                      2
                    </span>
                  </div>
                  <div>
                    <h4 className="font-jakarta font-600 text-foreground mb-2">
                      Preparation Checklist
                    </h4>
                    <p className="font-inter font-500 text-sm text-muted-foreground">
                      We'll send you a preparation guide to maximize your demo
                      experience
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-accent/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="font-jakarta font-700 text-sm text-accent">
                      3
                    </span>
                  </div>
                  <div>
                    <h4 className="font-jakarta font-600 text-foreground mb-2">
                      Personalized Demo
                    </h4>
                    <p className="font-inter font-500 text-sm text-muted-foreground">
                      45-minute tailored demonstration based on your
                      institution's needs
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <Button
              variant="outline"
              size="lg"
              onClick={() => window.location?.reload()}
              className="font-jakarta font-600"
              iconName="ArrowLeft"
              iconPosition="left"
              iconSize={18}
            >
              Back to Homepage
            </Button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      ref={sectionRef}
      id="demo-booking"
      className="py-20 bg-gradient-to-b from-surface to-background"
    >
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div
          className={`text-center mb-12 sm:mb-16 transform transition-all duration-1000 ${
            isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"
          }`}
        >
          <div className="inline-flex items-center space-x-2 bg-primary/10 text-primary px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-inter font-600 mb-4 sm:mb-6">
            <Icon name="Calendar" size={16} color="currentColor" />
            <span>Book Your Personalized Demo</span>
          </div>

          <h2 className="font-jakarta font-normal text-2xl sm:text-3xl lg:text-5xl text-foreground mb-4 sm:mb-6">
            See KnowTran in Action for{" "}
            <span className="bg-gradient-to-r from-primary font-normal to-secondary bg-clip-text text-transparent">
              Your Institution
            </span>
          </h2>

          <p className="font-inter font-500 text-base sm:text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            Schedule a 45-minute personalized demonstration tailored to your
            institution's specific needs and challenges. No generic
            presentations - just solutions for your real problems.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-6 lg:gap-12">
          {/* Left - Demo Benefits */}
          <div
            className={`space-y-6 sm:space-y-8 transform transition-all duration-1000 delay-300 ${
              isVisible
                ? "translate-x-0 opacity-100"
                : "-translate-x-8 opacity-0"
            }`}
          >
            <div>
              <h3 className="font-jakarta font-700 text-xl sm:text-2xl text-foreground mb-4 sm:mb-6">
                What You'll Experience in Your Demo
              </h3>

              <div className="space-y-6">
                {[
                  {
                    title: "Product Walkthrough",
                    description:
                      "See all features and how they work for your institution type",
                    icon: "Play",
                    color: "primary",
                  },
                  {
                    title: "Customized Demo",
                    description:
                      "Tailored to your specific challenges and institution size",
                    icon: "Target",
                    color: "success",
                  },
                  {
                    title: "Q&A Session",
                    description:
                      "Ask questions about features, pricing, and implementation",
                    icon: "MessageCircle",
                    color: "accent",
                  },
                  {
                    title: "Implementation Planning",
                    description:
                      "Discuss timeline and next steps for your institution",
                    icon: "Calendar",
                    color: "warning",
                  },
                ]?.map((benefit, index) => (
                  <div
                    key={index}
                    className="flex items-start space-x-3.5 sm:space-x-4"
                  >
                    <div
                      className={`w-9 h-9 sm:w-12 sm:h-12 rounded-xl bg-${benefit?.color}/10 flex items-center justify-center flex-shrink-0`}
                    >
                      <Icon
                        name={benefit?.icon}
                        size={18}
                        color={`var(--color-${benefit?.color})`}
                      />
                    </div>
                    <div>
                      <h4 className="font-jakarta font-700 text-base sm:text-lg text-foreground mb-1.5 sm:mb-2">
                        {benefit?.title}
                      </h4>
                      <p className="font-inter font-500 text-sm sm:text-base text-muted-foreground">
                        {benefit?.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Demo Stats */}
            <div className="bg-gradient-to-r from-primary/10 to-secondary/10 rounded-2xl p-5 sm:p-6 border border-primary/20">
              <h4 className="font-jakarta font-700 text-base sm:text-lg text-foreground mb-3 sm:mb-4">
                What to Expect
              </h4>
              <div className="grid grid-cols-2 gap-3 sm:gap-4">
                <div className="text-center">
                  <div className="font-jakarta font-800 text-xl sm:text-2xl text-primary">
                    45 min
                  </div>
                  <div className="font-inter font-500 text-xs sm:text-sm text-muted-foreground">
                    Demo Duration
                  </div>
                </div>
                <div className="text-center">
                  <div className="font-jakarta font-800 text-xl sm:text-2xl text-success">
                    Free
                  </div>
                  <div className="font-inter font-500 text-xs sm:text-sm text-muted-foreground">
                    No Cost
                  </div>
                </div>
                <div className="text-center">
                  <div className="font-jakarta font-800 text-xl sm:text-2xl text-accent">
                    1-on-1
                  </div>
                  <div className="font-inter font-500 text-xs sm:text-sm text-muted-foreground">
                    Personalized
                  </div>
                </div>
                <div className="text-center">
                  <div className="font-jakarta font-800 text-xl sm:text-2xl text-warning">
                    24hrs
                  </div>
                  <div className="font-inter font-500 text-xs sm:text-sm text-muted-foreground">
                    Follow-up
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right - Booking Form */}
          <div
            className={`transform transition-all duration-1000 delay-500 ${
              isVisible
                ? "translate-x-0 opacity-100"
                : "translate-x-8 opacity-0"
            }`}
          >
            <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-5 sm:p-6 md:p-8 border border-white/20 shadow-2xl aurora-glow">
              <form onSubmit={handleSubmit} className="space-y-5 sm:space-y-6">
                {/* Institution Details */}
                <div className="space-y-3.5 sm:space-y-4">
                  <h4 className="font-jakarta font-700 text-base sm:text-lg text-foreground">
                    Institution Details
                  </h4>

                  <Input
                    label="Institution Name"
                    type="text"
                    placeholder="Enter your institution name"
                    value={formData?.institutionName}
                    onChange={(e) =>
                      handleInputChange("institutionName", e?.target?.value)
                    }
                    error={errors?.institutionName}
                    required
                  />

                  <div className="grid md:grid-cols-2 gap-3.5 sm:gap-4">
                    <Select
                      label="Institution Type"
                      options={institutionTypes}
                      value={formData?.institutionType}
                      onChange={(e) =>
                        handleInputChange(
                          "institutionType",
                          (e.target as HTMLSelectElement).value
                        )
                      }
                      placeholder="Select institution type"
                      required
                    />

                    <Select
                      label="Student Count"
                      options={studentCountRanges}
                      value={formData?.studentCount}
                      onChange={(e) =>
                        handleInputChange(
                          "studentCount",
                          (e.target as HTMLSelectElement).value
                        )
                      }
                      placeholder="Select student count"
                      required
                    />
                  </div>

                  {formData.institutionType === "other" && (
                    <Input
                      label="Specify Institution Type"
                      type="text"
                      placeholder="e.g., Training Center"
                      value={formData.institutionTypeOther}
                      onChange={(e) =>
                        handleInputChange(
                          "institutionTypeOther",
                          e.target.value
                        )
                      }
                      error={errors["institutionTypeOther"]}
                      required
                    />
                  )}
                </div>

                {/* Contact Information */}
                <div className="space-y-3.5 sm:space-y-4">
                  <h4 className="font-jakarta font-700 text-base sm:text-lg text-foreground">
                    Contact Information
                  </h4>

                  <Input
                    label="Your Name"
                    type="text"
                    placeholder="Enter your full name"
                    value={formData?.contactName}
                    onChange={(e) =>
                      handleInputChange("contactName", e?.target?.value)
                    }
                    error={errors?.contactName}
                    required
                  />

                  <div className="grid md:grid-cols-2 gap-3.5 sm:gap-4">
                    <Input
                      label="Email Address"
                      type="email"
                      placeholder="your.email@institution.edu"
                      value={formData?.email}
                      onChange={(e) =>
                        handleInputChange("email", e?.target?.value)
                      }
                      error={errors?.email}
                      required
                    />

                    <Input
                      label="Phone Number"
                      type="tel"
                      placeholder="+1 (555) 123-4567"
                      value={formData?.phone}
                      onChange={(e) =>
                        handleInputChange("phone", e?.target?.value)
                      }
                      error={errors?.phone}
                      required
                    />
                  </div>
                </div>

                {/* Current Situation */}
                <div className="space-y-3.5 sm:space-y-4">
                  <h4 className="font-jakarta font-700 text-base sm:text-lg text-foreground">
                    Current Situation
                  </h4>

                  <div className="grid md:grid-cols-2 gap-3.5 sm:gap-4">
                    <Select
                      label="Current System"
                      options={currentSystems}
                      value={formData?.currentSystem}
                      onChange={(e) =>
                        handleInputChange(
                          "currentSystem",
                          (e.target as HTMLSelectElement).value
                        )
                      }
                      placeholder="Select current system (optional)"
                    />

                    <Select
                      label="Primary Challenge"
                      options={primaryChallenges}
                      value={formData?.primaryChallenge}
                      onChange={(e) =>
                        handleInputChange(
                          "primaryChallenge",
                          (e.target as HTMLSelectElement).value
                        )
                      }
                      placeholder="Select primary challenge (optional)"
                    />
                  </div>

                  {formData.currentSystem === "other" && (
                    <Input
                      label="Specify Current System"
                      type="text"
                      placeholder="What system are you using?"
                      value={formData.currentSystemOther}
                      onChange={(e) =>
                        handleInputChange("currentSystemOther", e.target.value)
                      }
                      error={errors["currentSystemOther"]}
                      required
                    />
                  )}

                  {formData.primaryChallenge === "other" && (
                    <Input
                      label="Specify Primary Challenge"
                      type="text"
                      placeholder="What's your primary challenge?"
                      value={formData.primaryChallengeOther}
                      onChange={(e) =>
                        handleInputChange(
                          "primaryChallengeOther",
                          e.target.value
                        )
                      }
                      error={errors["primaryChallengeOther"]}
                      required
                    />
                  )}
                </div>

                {/* Scheduling */}
                <div className="space-y-3.5 sm:space-y-4">
                  <h4 className="font-jakarta font-700 text-base sm:text-lg text-foreground">
                    Preferred Schedule
                  </h4>

                  <div className="grid md:grid-cols-2 gap-3.5 sm:gap-4">
                    <Input
                      label="Preferred Date"
                      type="date"
                      value={formData?.preferredDate}
                      onChange={(e) =>
                        handleInputChange("preferredDate", e?.target?.value)
                      }
                      error={errors?.preferredDate}
                      min={getTomorrowDate()}
                      required
                    />

                    <Select
                      label="Preferred Time"
                      options={timeSlots}
                      value={formData?.preferredTime}
                      onChange={(e) =>
                        handleInputChange(
                          "preferredTime",
                          (e.target as HTMLSelectElement).value
                        )
                      }
                      placeholder="Select time"
                      required
                    />
                  </div>
                </div>

                {/* Additional Notes */}
                <div>
                  <label className="block font-inter font-600 text-sm sm:text-base text-foreground mb-1.5 sm:mb-2">
                    Additional Notes (Optional)
                  </label>
                  <textarea
                    placeholder="Any specific features you'd like to see or questions you have?"
                    value={formData?.additionalNotes}
                    onChange={(e) =>
                      handleInputChange("additionalNotes", e?.target?.value)
                    }
                    rows={3}
                    className="w-full px-3.5 sm:px-4 py-2.5 sm:py-3 border border-border rounded-xl bg-input text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent resize-none"
                  />
                </div>

                {/* Submit Button */}
                <Button
                  type="submit"
                  variant="default"
                  size="lg"
                  fullWidth
                  loading={isSubmitting}
                  className="font-jakarta font-700 text-base sm:text-lg aurora-glow"
                  iconName="Calendar"
                  iconPosition="left"
                  iconSize={20}
                >
                  {isSubmitting
                    ? "Booking Your Demo..."
                    : "Book My Personalized Demo"}
                </Button>

                {/* Privacy Notice */}
                <div className="text-center pt-3.5 sm:pt-4 border-t border-white/20">
                  <p className="font-inter font-400 text-xs sm:text-sm text-muted-foreground">
                    By booking a demo, you agree to our{" "}
                    <Link
                      to="/privacy-policy"
                      className="font-inter font-500 text-primary hover:text-primary transition-colors duration-300"
                    >
                      Privacy Policy
                    </Link>
                    . We'll only use your information to provide the demo and
                    follow-up.
                  </p>
                </div>
              </form>
            </div>

            {/* Trust Indicators */}
            <div className="mt-6 sm:mt-8 grid grid-cols-2 sm:grid-cols-3 gap-3.5 sm:gap-4">
              <div className="text-center p-3.5 sm:p-4 bg-white/60 backdrop-blur-sm rounded-xl border border-white/20">
                <Icon
                  name="Shield"
                  size={22}
                  color="var(--color-success)"
                  className="mx-auto mb-1.5 sm:mb-2"
                />
                <div className="font-inter font-600 text-xs sm:text-sm text-foreground">
                  GDPR Compliant
                </div>
              </div>
              <div className="text-center p-3.5 sm:p-4 bg-white/60 backdrop-blur-sm rounded-xl border border-white/20">
                <Icon
                  name="Clock"
                  size={22}
                  color="var(--color-primary)"
                  className="mx-auto mb-1.5 sm:mb-2"
                />
                <div className="font-inter font-600 text-xs sm:text-sm text-foreground">
                  45 Min Demo
                </div>
              </div>
              <div className="text-center p-3.5 sm:p-4 bg-white/60 backdrop-blur-sm rounded-xl border border-white/20">
                <Icon
                  name="Users"
                  size={22}
                  color="var(--color-accent)"
                  className="mx-auto mb-1.5 sm:mb-2"
                />
                <div className="font-inter font-600 text-xs sm:text-sm text-foreground">
                  Expert Guide
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Alternative Contact Methods */}
        <div
          className={`mt-14 sm:mt-16 text-center transform transition-all duration-1000 delay-700 ${
            isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"
          }`}
        >
          <div className="bg-gradient-to-r from-muted/30 to-muted/10 rounded-2xl p-6 sm:p-8 border border-white/20">
            <h3 className="font-jakarta font-700 text-lg sm:text-xl text-foreground mb-3 sm:mb-4">
              Prefer a Different Approach?
            </h3>
            <p className="font-inter font-500 text-sm sm:text-base text-muted-foreground mb-5 sm:mb-6">
              We're flexible with how you'd like to learn about KnowTran
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-center">
              <a
                href="tel:+919150080348"
                className="inline-flex items-center space-x-2 bg-white text-primary px-5 sm:px-6 py-2.5 sm:py-3 rounded-xl font-jakarta font-600 text-sm sm:text-base border-2 border-primary/20 hover:bg-primary/5 transition-all duration-300"
              >
                <Icon name="Phone" size={18} color="currentColor" />
                <span>Call Us: +91 9150080348</span>
              </a>
              {/* 
              <button className="inline-flex items-center space-x-2 bg-white text-primary px-6 py-3 rounded-xl font-jakarta font-600 border-2 border-primary/20 hover:bg-primary/5 transition-all duration-300">
                <Icon name="MessageSquare" size={18} color="currentColor" />
                <span>Live Chat Support</span>
              </button>

              <button
                onClick={() =>
                  document
                    .getElementById("product-tour")
                    ?.scrollIntoView({ behavior: "smooth" })
                }
                className="inline-flex items-center space-x-2 bg-white text-primary px-6 py-3 rounded-xl font-jakarta font-600 border-2 border-primary/20 hover:bg-primary/5 transition-all duration-300"
              >
                <Icon name="MousePointer" size={18} color="currentColor" />
                <span>Interactive Tour</span>
              </button> */}
            </div>
          </div>
        </div>
      </div>

      {toast && (
        <div
          className={`fixed md:top-16 md:right-6 left-1/2 -translate-x-1/2 md:left-auto md:translate-x-0 bottom-6 md:bottom-auto z-50 rounded-xl px-4 py-3 shadow-lg border w-[calc(100%-2rem)] max-w-sm ${
            toast.type === "success"
              ? "bg-green-50 border-green-200 text-green-800"
              : "bg-red-50 border-red-200 text-red-800"
          }`}
          role="status"
          aria-live="polite"
        >
          {toast.message}
        </div>
      )}
    </section>
  );
};

export default DemoBookingSection;
